import { useCallback, useEffect, useState } from 'react';
import {
    useNavigate, useParams, useSearchParams,
} from 'react-router-dom';
import { useAuthContext } from 'contexts/AuthContext';
import { useGetProForma } from 'queries/ProForma';
import { FEATURE_IDS } from 'services/constants';
import { AccessLevels } from 'types/auth';
import { useGetBackOfEnvelope } from 'views/DealDashboard/BOE/hooks/useGetBOE';
import { BOEScenario } from 'views/DealDashboard/BOE/types';
import ProFormaSideNav from './components/ProFormaSideNav/ProFormaSideNav';
import {
    PRO_FORMA_TABS,
    PRO_FORMA_TAB_NAMES,
    PRO_FORMA_TABLE_NAV,
    PRO_FORMA_TABLE_NAV_NAMES,
    RETURNS_AND_WATERFALL_TABS,
    RETURNS_AND_WATERFALL_TAB_NAMES,
} from './constants';
import RenderIf from '../../components/RenderIf/RenderIf';
import ProForma from './components/ProForma/ProForma';
import { ProFormaCalculatingProgressBar } from './components/ProForma/CalculatingProgressBar';
import ReturnsAndWaterfall from './components/ReturnsAndWaterfall/ReturnsAndWaterfall';
import ExecutiveSummary from './components/ExecutiveSummary/ExecutiveSummary';
import { getProFormaUrl, checkUsesSourcesUnequal } from './helpers';
import Sensitivity from './components/Sensitivity/Sensitivity';
import { ProFormaSocketProvider, useProFormaSocketContext } from './components/ProForma/socketContext/ProFormaSocketProvider';

interface IProFormaTableContentProps {
    userHasAccess: boolean;
    activeSideNav: string;
    navigateToSection: (sideNavName: string, tabName?: string) => void;
    section: string;
    tab: string;
    boeScenario?: BOEScenario;
    dealId?: string;
    proFormaId?: string;
}

const ProFormaTableContent = ({
    userHasAccess,
    activeSideNav,
    navigateToSection,
    section,
    tab,
    boeScenario,
    dealId,
    proFormaId,
}: IProFormaTableContentProps) => {
    const { shouldUseProjectActuals } = useProFormaSocketContext();

    const hasWriteAccessToProForma = shouldUseProjectActuals ? false : userHasAccess;

    return (
        <>
            <ProFormaCalculatingProgressBar />
            <div className="flex-shrink-0">
                <ProFormaSideNav
                    activeNav={activeSideNav}
                    navigateToSection={navigateToSection}
                    hasWriteAccessToProForma={hasWriteAccessToProForma}
                />
            </div>

            <div className="flex-grow-1 overflow-hidden ml-3">
                <RenderIf isTrue={activeSideNav === PRO_FORMA_TABLE_NAV.PRO_FORMA.name}>
                    <ProForma
                        navigateToSection={navigateToSection}
                        currentSection={section}
                        currentTab={tab}
                        hasWriteAccessToProForma={hasWriteAccessToProForma}
                        boeScenario={boeScenario}
                    />
                </RenderIf>

                <RenderIf isTrue={activeSideNav === PRO_FORMA_TABLE_NAV.RETURNS_AND_WATERFALL.name}>
                    <ReturnsAndWaterfall
                        navigateToSection={navigateToSection}
                        currentSection={section}
                        currentTab={tab}
                        hasWriteAccessToProForma={hasWriteAccessToProForma}
                    />
                </RenderIf>

                <RenderIf isTrue={activeSideNav === PRO_FORMA_TABLE_NAV.SENSITIVITY.name}>
                    <Sensitivity
                        hasWriteAccessToProForma={hasWriteAccessToProForma}
                        dealId={Number(dealId)}
                        proFormaId={Number(proFormaId)}
                    />
                </RenderIf>

                <RenderIf isTrue={activeSideNav === PRO_FORMA_TABLE_NAV.EXECUTIVE_SUMMARY.name}>
                    {dealId && proFormaId && (
                        <ExecutiveSummary
                            dealId={+dealId}
                            proFormaId={+proFormaId}
                            hasWriteAccessToProForma={hasWriteAccessToProForma}
                        />
                    )}
                </RenderIf>
            </div>
        </>
    );
};

const ProFormaTable = () => {
    const navigate = useNavigate();
    const { selectedOrganizationId, isFeatureAccessibleToUser } = useAuthContext();
    const userHasAccess = isFeatureAccessibleToUser([FEATURE_IDS.PRO_FORMA], AccessLevels.READ_WRITE_ACCESS);
    const { uses, sources } = useProFormaSocketContext();

    const [searchParams, setSearchParams] = useSearchParams();
    const { dealId, proFormaId } = useParams();

    const section = searchParams.get('section') || '';
    const tab = searchParams.get('tab') || '';

    const [activeSideNav, setActiveSideNav] = useState<string>(section);

    const { data: proForma, refetch } = useGetProForma(Number(dealId), Number(proFormaId), {
        enabled: Boolean(dealId) && Boolean(proFormaId),
    });

    const { data: boeScenario } = useGetBackOfEnvelope({
        orgId: selectedOrganizationId!,
        scenarioId: Number(proForma?.boeScenario?.id || 0),
        enabled: !!selectedOrganizationId && !!proForma?.boeScenario?.id,
    });

    useEffect(() => {
        if (dealId && proFormaId) {
            refetch();
        }
    }, [dealId, proFormaId, refetch]);

    const navigateToSection = useCallback(
        (sideNavName: string, tabName?: string) => {
            if (checkUsesSourcesUnequal(uses, sources) && sideNavName === PRO_FORMA_TABLE_NAV.RETURNS_AND_WATERFALL.name) {
                setActiveSideNav(PRO_FORMA_TABLE_NAV.PRO_FORMA.name);
                setSearchParams({
                    section: PRO_FORMA_TABLE_NAV.PRO_FORMA.name,
                    tab: PRO_FORMA_TABS.DEVELOPMENT.name,
                });
                navigate(getProFormaUrl(PRO_FORMA_TABLE_NAV.PRO_FORMA.name, PRO_FORMA_TABS.DEVELOPMENT.name), { replace: true });
                return;
            }

            setActiveSideNav(sideNavName);

            const updatedSearchParams: Record<string, string> = {
                section: sideNavName,
                ...(tabName ? { tab: tabName } : {}),
            };
            setSearchParams(updatedSearchParams);
            navigate(getProFormaUrl(sideNavName, tabName), { replace: true });
        },
        [setActiveSideNav, setSearchParams, navigate, uses, sources],
    );

    useEffect(() => {
        const currentSection = PRO_FORMA_TABLE_NAV_NAMES.includes(section) ? section : PRO_FORMA_TABLE_NAV.PRO_FORMA.name;
        let currentTab = '';
        if (currentSection === PRO_FORMA_TABLE_NAV.PRO_FORMA.name) {
            currentTab = tab && PRO_FORMA_TAB_NAMES.includes(tab) ? tab : PRO_FORMA_TABS.GENERAL_INFORMATION.name;
        } else if (currentSection === PRO_FORMA_TABLE_NAV.RETURNS_AND_WATERFALL.name) {
            currentTab = tab && RETURNS_AND_WATERFALL_TAB_NAMES.includes(tab) ? tab : RETURNS_AND_WATERFALL_TABS.RETURNS;
        }
        if (currentSection !== section || currentTab !== tab) {
            navigateToSection(currentSection, currentTab);
        }
    }, [section, tab, navigateToSection]);

    return (
        <div className="d-flex align-items-stretch">
            <RenderIf isTrue={proForma}>
                {proForma && (
                    <ProFormaSocketProvider
                        organizationId={selectedOrganizationId!}
                        dealId={proForma.dealId}
                        proFormaId={proForma.id}
                    >
                        <ProFormaTableContent
                            userHasAccess={userHasAccess}
                            activeSideNav={activeSideNav}
                            navigateToSection={navigateToSection}
                            section={section}
                            tab={tab}
                            boeScenario={boeScenario}
                            dealId={dealId}
                            proFormaId={proFormaId}
                        />
                    </ProFormaSocketProvider>
                )}
            </RenderIf>
        </div>
    );
};

export default ProFormaTable;
